import {
	fromCallback, fromPromise,
} from 'xstate';
import {
	type CreateLocalPhotoOutput,
	type ProcessUploadedPhotoOutput,
	type UploadError,
	type UploadItemActorInput,
	type UploadItemContext,
	type UploadItemEvent,
	validateUploadInput,
} from './machines';

export type UploadFileAdapterLifecycle = {
	abortSignal: AbortSignal;
	reportProgress: (uploadedBytes: number, totalBytes: number) => void;
	complete: (uploadUrl: string) => void;
	fail: (error: UploadError) => void;
};

export type UploadFileAdapterCleanup = () => void;

export type UploadFileAdapterResult =
	| void
	| UploadFileAdapterCleanup
	| Promise<void | UploadFileAdapterCleanup>;

export type UploadItemAdapters = {
	validateUpload?: (input: UploadItemContext) => void | Promise<void>;
	createLocalPhoto: (input: UploadItemContext) => CreateLocalPhotoOutput | Promise<CreateLocalPhotoOutput>;
	uploadFile: (input: UploadItemContext, lifecycle: UploadFileAdapterLifecycle) => UploadFileAdapterResult;
	processUploadedPhoto: (
		input: UploadItemContext,
	) => ProcessUploadedPhotoOutput | Promise<ProcessUploadedPhotoOutput>;
};

export function createUploadItemActors(adapters: UploadItemAdapters) {
	return {
		validateUpload: fromPromise(async ({ input }: { input: UploadItemActorInput }): Promise<void> => {
			validateUploadInput(input);

			await adapters.validateUpload?.(input);
		}),
		createLocalPhoto: fromPromise(
			async ({ input }: { input: UploadItemActorInput }): Promise<CreateLocalPhotoOutput> => (
				adapters.createLocalPhoto(input)
			),
		),
		uploadFile: fromCallback<UploadItemEvent, UploadItemActorInput>(({
			input,
			sendBack,
		}) => {
			const abortController = new AbortController();
			let cleanup: UploadFileAdapterCleanup | null = null;
			let stopped = false;

			const lifecycle: UploadFileAdapterLifecycle = {
				abortSignal: abortController.signal,
				reportProgress: (uploadedBytes, totalBytes) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.progress',
						uploadedBytes,
						totalBytes,
					});
				},
				complete: (uploadUrl) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.completed',
						uploadUrl,
					});
				},
				fail: (error) => {
					if (stopped) {
						return;
					}

					sendBack({
						type: 'upload.failed',
						error,
					});
				},
			};

			const failUpload = (error: unknown) => {
				if (stopped || abortController.signal.aborted) {
					return;
				}

				/*
				 * Upload errors default to retryable=true (network blips, S3
				 * throttling, etc.). Adapters that throw a deterministic failure
				 * (bucket gone, permanently expired signed url) attach
				 * `retryable: false` to the Error instance so we honor that — this
				 * mirrors setThrownFailure's handling of processing errors so a
				 * deterministic failure does not needlessly burn the retry budget.
				 */
				const retryable = (
					error instanceof Error
					&& 'retryable' in error
					&& typeof (error as { retryable?: unknown }).retryable === 'boolean'
				)
					? (error as { retryable: boolean }).retryable
					: true;

				sendBack({
					type: 'upload.failed',
					error: {
						kind: 'upload',
						message: error instanceof Error ? error.message : String(error),
						retryable,
					},
				});
			};

			let adapterResult: UploadFileAdapterResult;

			try {
				adapterResult = adapters.uploadFile(
					input,
					lifecycle,
				);
			} catch (error: unknown) {
				failUpload(error);

				/*
				 * Identical disposer to the normal-return path below: stop further
				 * lifecycle events, abort the adapter's signal, and run any cleanup
				 * it managed to register. A synchronous throw means `cleanup` is
				 * still null here, but keeping both disposers in lockstep avoids the
				 * fragility of two diverging teardown paths.
				 */
				return () => {
					stopped = true;
					abortController.abort();
					cleanup?.();
				};
			}

			Promise.resolve(adapterResult)
				.then((adapterCleanup) => {
					if (typeof adapterCleanup !== 'function') {
						return;
					}

					/*
					 * The actor may have been disposed before this async cleanup
					 * resolved. In that case the disposer below already ran (so it
					 * could not see `cleanup`), and we must invoke the late cleanup
					 * ourselves. Otherwise stash it for the disposer to call.
					 */
					if (stopped) {
						adapterCleanup();
						return;
					}

					cleanup = adapterCleanup;
				})
				.catch(failUpload);

			/*
			 * Disposer: set `stopped` first so any in-flight lifecycle callbacks
			 * and the late-cleanup branch above turn into no-ops / self-cleanup,
			 * then abort the adapter's signal and run whatever cleanup it
			 * registered synchronously.
			 */
			return () => {
				stopped = true;
				abortController.abort();
				cleanup?.();
			};
		}),
		processUploadedPhoto: fromPromise(
			async ({ input }: { input: UploadItemActorInput }): Promise<ProcessUploadedPhotoOutput> => (
				adapters.processUploadedPhoto(input)
			),
		),
	};
}
